import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc, 
  updateDoc,
  query,
  orderBy 
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../utils/firebaseConfig';
import GeneralNavbar from './GeneralNavbar';
import './ManageCategories.css';

const ManageCategories = () => {
  const navigate = useNavigate();
  const [allCategories, setAllCategories] = useState([]); // Todas las categorías (solo nombres)
  const [selectedCategory, setSelectedCategory] = useState(null); // Categoría seleccionada con detalles
  const [loading, setLoading] = useState(true);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({ name: '', image: '', imageFile: null });
  const [brandData, setBrandData] = useState({ name: '', image: '', imageFile: null });
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleBackClick = () => {
    navigate('/admin');
  };

  // 🔹 Cargar lista de TODAS las categorías (sin marcas)
  const loadAllCategories = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'categories'),
        orderBy('order', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      const categoriesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        order: doc.data().order
      }));

      setAllCategories(categoriesList);
    } catch (error) {
      console.error("Error cargando categorías:", error);
      alert("Error al cargar categorías");
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔹 Cargar categoría seleccionada CON sus marcas
  const loadCategoryDetails = useCallback(async (categoryId) => {
    try {
      setLoadingCategory(true);
      
      // Obtener detalles de la categoría
      const categoryDoc = await getDocs(query(
        collection(db, 'categories'),
        orderBy('order', 'asc')
      ));
      
      const category = categoryDoc.docs.find(doc => doc.id === categoryId);
      
      if (!category) return;
      
      // Cargar marcas
      const brandsSnapshot = await getDocs(
        collection(db, 'categories', categoryId, 'brands')
      );
      
      const brands = brandsSnapshot.docs.map(brandDoc => ({
        id: brandDoc.id,
        ...brandDoc.data()
      }));

      setSelectedCategory({
        id: category.id,
        ...category.data(),
        brands: brands
      });
    } catch (error) {
      console.error("Error cargando categoría:", error);
      alert("Error al cargar categoría");
    } finally {
      setLoadingCategory(false);
    }
  }, []);

  // 🔹 useEffect para cargar lista inicial
  useEffect(() => {
    loadAllCategories();
  }, [loadAllCategories]);

  // 🔹 Manejar selección de categoría
  const handleCategorySelect = (categoryId) => {
    if (categoryId) {
      loadCategoryDetails(categoryId);
    } else {
      setSelectedCategory(null);
    }
  };

  // 🔹 Agregar/Actualizar categoría
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (!formData.name.trim()) {
        alert("El nombre de la categoría es requerido");
        return;
      }

      if (editingCategory) {
        // Actualizar (sin cambiar orden)
        await updateDoc(doc(db, 'categories', editingCategory), {
          name: formData.name
        });
        alert("✅ Categoría actualizada");
      } else {
        // Crear - calcular orden automáticamente
        let newOrder = 0;
        const q = query(collection(db, 'categories'), orderBy('order', 'desc'));
        const snapshot = await getDocs(q);
        
        if (snapshot.docs.length > 0) {
          const lastOrder = snapshot.docs[0].data().order || 0;
          newOrder = lastOrder + 1;
        }

        await addDoc(collection(db, 'categories'), {
          name: formData.name,
          order: newOrder,
          createdAt: new Date()
        });
        alert("✅ Categoría creada correctamente");
      }

      setFormData({ name: '', image: '', imageFile: null });
      setEditingCategory(null);
      loadAllCategories();
    } catch (error) {
      console.error("Error guardando categoría:", error);
      alert("Error al guardar categoría");
    }
  };

  // 🔹 Eliminar categoría
  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("¿Eliminar esta categoría y todas sus marcas?")) return;
    
    try {
      // Primero eliminar todas las marcas
      const brandsSnapshot = await getDocs(
        collection(db, 'categories', categoryId, 'brands')
      );
      
      for (const brandDoc of brandsSnapshot.docs) {
        await deleteDoc(doc(db, 'categories', categoryId, 'brands', brandDoc.id));
      }

      // Luego eliminar la categoría
      await deleteDoc(doc(db, 'categories', categoryId));
      alert("✅ Categoría eliminada");
      setSelectedCategory(null);
      loadAllCategories();
    } catch (error) {
      console.error("Error eliminando categoría:", error);
      alert("Error al eliminar");
    }
  };

  // 🔹 Agregar/Actualizar marca
  const handleSaveBrand = async (e, categoryId) => {
    e.preventDefault();
    try {
      if (!brandData.name.trim()) {
        alert("El nombre de la marca es requerido");
        return;
      }

      if (!editingBrand && !brandData.imageFile) {
        alert("La imagen de la marca es requerida");
        return;
      }

      let imageURL = brandData.image || '';

      // Si hay archivo de imagen, subirlo
      if (brandData.imageFile) {
        setUploadingImage(true);
        try {
          imageURL = await uploadImage(brandData.imageFile, `categories/${categoryId}/brands`);
        } catch (error) {
          alert("Error al subir la imagen");
          setUploadingImage(false);
          return;
        }
        setUploadingImage(false);
      }

      if (editingBrand) {
        // Actualizar
        await updateDoc(
          doc(db, 'categories', categoryId, 'brands', editingBrand),
          { name: brandData.name, image: imageURL }
        );
        alert("✅ Marca actualizada");
      } else {
        // Crear
        await addDoc(
          collection(db, 'categories', categoryId, 'brands'),
          { name: brandData.name, image: imageURL, createdAt: new Date() }
        );
        alert("✅ Marca agregada correctamente");
      }

      setBrandData({ name: '', image: '', imageFile: null });
      setEditingBrand(null);
      if (selectedCategory) {
        loadCategoryDetails(selectedCategory.id);
      }
    } catch (error) {
      console.error("Error guardando marca:", error);
      alert("Error al guardar marca");
    }
  };

  // 🔹 Eliminar marca
  const handleDeleteBrand = async (categoryId, brandId) => {
    if (!window.confirm("¿Eliminar esta marca?")) return;
    
    try {
      await deleteDoc(doc(db, 'categories', categoryId, 'brands', brandId));
      alert("✅ Marca eliminada");
      loadCategoryDetails(categoryId);
    } catch (error) {
      console.error("Error eliminando marca:", error);
      alert("Error al eliminar");
    }
  };

  const startEditCategory = (category) => {
    setEditingCategory(category.id);
    setFormData({
      name: category.name,
      image: category.image || '',
      imageFile: null
    });
  };

  const startEditBrand = (brand) => {
    setEditingBrand(brand.id);
    setBrandData({
      name: brand.name,
      image: brand.image || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setEditingBrand(null);
    setFormData({ name: '', image: '', imageFile: null });
    setBrandData({ name: '', image: '', imageFile: null });
  };

  // 🔹 Manejar carga de imagen para marcas
  const handleBrandImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBrandData({ ...brandData, imageFile: file });
    }
  };

  // 🔹 Subir imagen a Firebase Storage
  const uploadImage = async (file, folder) => {
    try {
      const timestamp = Date.now();
      const fileName = `${folder}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error("Error subiendo imagen:", error);
      throw error;
    }
  };

  return (
    <>
      <GeneralNavbar title="Gestionar Categorías y Marcas" onBackClick={handleBackClick} />
      
      <div className="manage-categories-wrapper">
        <div className="manage-categories">
          <h2>📁 Gestionar Categorías y Marcas</h2>

          {loading ? (
            <p style={{ textAlign: 'center', color: '#999' }}>Cargando categorías...</p>
          ) : (
            <>
              {/* 🔹 DROPDOWN DE CATEGORÍAS */}
              <div className="dropdown-section">
                <h3>🏷️ Seleccionar Categoría</h3>
                <select 
                  value={selectedCategory?.id || ''}
                  onChange={(e) => handleCategorySelect(e.target.value)}
                  className="category-dropdown"
                  disabled={loadingCategory}
                >
                  <option value="">-- Selecciona una categoría --</option>
                  {allCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Formulario de categorías */}
              <div className="form-section">
                <h3>{editingCategory ? '✏️ Editar Categoría' : '➕ Nueva Categoría'}</h3>
                <form onSubmit={handleSaveCategory}>
                  <input
                    type="text"
                    placeholder="Nombre de categoría"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />

                  <button type="submit">
                    {editingCategory ? 'Actualizar' : 'Crear'} Categoría
                  </button>
                  {editingCategory && (
                    <button 
                      type="button" 
                      onClick={handleCancelEdit}
                    >
                      Cancelar
                    </button>
                  )}
                </form>
              </div>

              {/* 🔹 DETALLES DE LA CATEGORÍA SELECCIONADA */}
              {loadingCategory ? (
                <p style={{ textAlign: 'center', color: '#999' }}>Cargando categoría...</p>
              ) : selectedCategory ? (
                <div className="categories-section">
                  <div className="category-card">
                    <div className="category-header">
                      <h4>{selectedCategory.name} (Orden: {selectedCategory.order})</h4>
                      <div className="category-actions">
                        <button onClick={() => startEditCategory(selectedCategory)} className="edit-btn">
                          ✏️ Editar
                        </button>
                        <button onClick={() => handleDeleteCategory(selectedCategory.id)} className="delete-btn">
                          🗑️ Eliminar
                        </button>
                      </div>
                    </div>

                    {/* Marcas de la categoría */}
                    <div className="brands-section">
                      <h5>🏷️ Marcas ({selectedCategory.brands.length})</h5>
                      
                      {/* Formulario de marca */}
                      <form onSubmit={(e) => handleSaveBrand(e, selectedCategory.id)} className="brand-form">
                        <input
                          type="text"
                          placeholder="Nombre de marca"
                          value={brandData.name}
                          onChange={(e) => setBrandData({ ...brandData, name: e.target.value })}
                          required
                        />
                        
                        {/* 🔹 Carga de imagen para marca */}
                        <div className="image-input-group">
                          <label htmlFor={`brand-image-${selectedCategory.id}`}>📷 Seleccionar imagen:</label>
                          <input
                            id={`brand-image-${selectedCategory.id}`}
                            type="file"
                            accept="image/*"
                            onChange={handleBrandImageChange}
                          />
                          {brandData.imageFile && (
                            <small style={{ color: 'green' }}>✓ {brandData.imageFile.name}</small>
                          )}
                        </div>

                        <button type="submit" disabled={uploadingImage}>
                          {uploadingImage ? '⏳ Subiendo...' : editingBrand ? 'Actualizar' : 'Agregar'}
                        </button>
                        {editingBrand && (
                          <button 
                            type="button" 
                            onClick={handleCancelEdit}
                          >
                            Cancelar
                          </button>
                        )}
                      </form>

                      {/* Lista de marcas */}
                      {selectedCategory.brands.length > 0 ? (
                        <ul className="brands-list">
                          {selectedCategory.brands.map((brand) => (
                            <li key={brand.id} className="brand-item">
                              {brand.image && (
                                <img src={brand.image} alt={brand.name} className="brand-logo" />
                              )}
                              <span>{brand.name}</span>
                              <div className="brand-actions">
                                <button 
                                  onClick={() => startEditBrand(brand)} 
                                  className="edit-btn"
                                >
                                  ✏️
                                </button>
                                <button 
                                  onClick={() => handleDeleteBrand(selectedCategory.id, brand.id)} 
                                  className="delete-btn"
                                >
                                  🗑️
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="no-brands">Sin marcas. ¡Agrega la primera!</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#999', padding: '30px' }}>
                  Selecciona una categoría del dropdown para ver sus detalles
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ManageCategories;
